export const GOOGLE_ADS_CONVERSION_TYPES = ["ggawc", "ggaoc", "ggalc", "ggass"];

export const DEV_MODE_MOCK_LOGIN_PARAMS = { code: "dev-mock-google-ads-code" };

export const DEV_MODE_MOCK_AUTHORIZE_RESPONSE = {
    status: true,
    data: {
        accounts: [
            {
                account_id: "8629914327",
                name: "TEST Account 61",
                is_manager: true,
                login_account_id: "8629914327",

                children: [
                    {
                        account_id: "1112223334",
                        name: "Child Store A",
                        is_manager: false,
                        login_account_id: "8629914327",
                        children: [],
                    },
                    {
                        account_id: "4445556667",
                        name: "Child Store B",
                        is_manager: false,
                        login_account_id: "8629914327",
                        children: [],
                    },
                ],
            },
            {
                account_id: "1268279925",
                name: "musa-sssa",
                is_manager: true,
                login_account_id: "1268279925",
                children: [],
            },
            {
                account_id: "7818936556",
                name: "TEST ACCOUNT",
                is_manager: true,
                login_account_id: "7818936556",
                children: [],
            },
        ],
        partner_install_id: 743,
    },
    message: "Success",
};

export const isGoogleAdsConversionCode = (code) =>
    GOOGLE_ADS_CONVERSION_TYPES.includes(code);

export const normalizeAccountTree = (nodes = []) =>
    (nodes ?? []).map((node) => ({
        accountId: String(node?.account_id ?? ""),
        name: node?.name ?? "",
        isManager: !!node?.is_manager,
        loginAccountId:
            node?.login_account_id != null
                ? String(node.login_account_id)
                : null,
        children: (node?.children ?? []).map((child) => ({
            accountId: String(child?.account_id ?? ""),
            name: child?.name ?? "",
            isManager: !!child?.is_manager,
            loginAccountId:
                child?.login_account_id != null
                    ? String(child.login_account_id)
                    : null,
        })),
    }));

export const buildSavePayload = ({ account, partnerInstallId, type }) => {
    const payload = {
        partner_install_id: partnerInstallId,
        account_id: account?.accountId,
    };

    if (
        account?.loginAccountId &&
        account.loginAccountId !== account.accountId
    ) {
        payload.login_account_id = account.loginAccountId;
    }

    if (type === "ggaoc" || type === "ggalc" || type === "ggass") {
        payload.type = type;
    }

    return payload;
};

export const STORE_SALES_ELIGIBILITY_MESSAGE =
    "This Google Ads account is not eligible for Store Sales conversions.";

export const GOOGLE_ADS_CONVERSIONS_FETCH_FALLBACK =
    "Conversion actions could not be fetched from Google.";

export const isStoreSalesEligibilityError = (message) =>
    String(message ?? "")
        .toLowerCase()
        .includes("not eligible for store sales");

export const getConversionsFetchErrorMessage = (type, rawMessage) => {
    if (type === "ggass" && isStoreSalesEligibilityError(rawMessage)) {
        return STORE_SALES_ELIGIBILITY_MESSAGE;
    }

    const trimmed = String(rawMessage ?? "").trim();
    return trimmed || GOOGLE_ADS_CONVERSIONS_FETCH_FALLBACK;
};

export const getConversionsLoadErrorDisplay = (loadError) => {
    if (isStoreSalesEligibilityError(loadError)) {
        return STORE_SALES_ELIGIBILITY_MESSAGE;
    }

    return `Conversion actions could not be loaded from Google: ${loadError}`;
};

export const getEmptyConversionsMessage = (type) => {
    if (type === "ggass") {
        return "This Google Ads account has no Store Sales conversion action, so there is nothing to map yet.";
    }

    return type === "ggaoc" || type === "ggalc"
        ? "This Google Ads account has no conversion action that accepts offline click uploads, so there is nothing to map yet. Create an offline conversion action in Google Ads, then reconnect this destination."
        : "This Google Ads account has no eligible website conversion action, so there is nothing to map yet. Only enabled website conversions that were set up with the Google Tag or Google Tag Manager can be used here; GA4 imported and URL-based conversions are not supported.";
};

export const getConversionLabel = (conversion) => {
    const name = conversion?.name ?? conversion?.conversion_name ?? "";
    const id = conversion?.id ?? conversion?.conversion_id ?? "";
    return id ? `${name} - ${id}` : name;
};

export const normalizeAutoMatchedEvents = (autoMatchedEvents = []) =>
    (autoMatchedEvents ?? [])
        .map((item) => ({
            event: String(item?.code ?? item?.event ?? ""),
            conversion_id: String(
                item?.conversion?.id ?? item?.conversion_id ?? "",
            ),
            conversion_name:
                item?.conversion?.name ?? item?.conversion_name ?? "",
        }))
        .filter((mapping) => mapping.event && mapping.conversion_id);

export const isValidGoogleAdsMapping = (
    mapping,
    conversions = [],
    { requireConversionName = false } = {},
) => {
    const event = String(mapping?.event ?? "").trim();
    const conversionId = String(mapping?.conversion_id ?? "").trim();
    const conversionName = String(mapping?.conversion_name ?? "").trim();

    if (!event || !conversionId) return false;

    const conversion = conversions.find(
        (conversion) => conversion?.id?.toString() === conversionId.toString(),
    );

    if (!conversion) return false;

    const resolvedConversionName = String(
        conversionName || conversion?.name || conversion?.conversion_name || "",
    ).trim();

    return !requireConversionName || !!resolvedConversionName;
};

export const areGoogleAdsMappingsValid = (
    mappings = [],
    conversions = [],
    type,
) => {
    if (!mappings.length) return false;

    const requiresStrictMapping = type === "ggalc" || type === "ggass";
    const areRowsValid = mappings.every((mapping) =>
        isValidGoogleAdsMapping(mapping, conversions, {
            requireConversionName: requiresStrictMapping,
        }),
    );

    if (!areRowsValid || !requiresStrictMapping) return areRowsValid;

    const events = mappings.map((mapping) => String(mapping.event).trim());
    return new Set(events).size === events.length;
};
